import type { EmployeeInvitationView, EmployeePlatform } from '@embertime/shared'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Link } from '@tanstack/react-router'
import { Copy, Lock, Plus, Search, X } from 'lucide-react'
import QRCode from 'qrcode'
import { useEffect, useRef, useState } from 'react'
import { API_BASE_URL, api, apiFetch } from '../../lib/api'

async function request<T>(path: string, body?: unknown): Promise<T> {
  const response = await apiFetch(`${API_BASE_URL}/api/${path}`, {
    credentials: 'include',
    cache: 'no-store',
    ...(body !== undefined
      ? {
          method: 'POST',
          headers: { 'content-type': 'application/json' },
          body: JSON.stringify(body),
        }
      : {}),
  })
  const data = await response.json()
  if (!response.ok) throw new Error(data.error ?? 'Vorgang fehlgeschlagen.')
  return data as T
}
const platforms: Record<EmployeePlatform, string> = {
  auto: 'Automatisch erkennen',
  ios: 'iPhone / iPad',
  android: 'Android',
  windows: 'Windows',
  macos: 'Mac',
}
const inputClass =
  'w-full rounded-xl border border-border-default bg-bg-overlay/50 px-3 py-2 text-sm min-w-0'
const buttonClass = 'rounded-xl border border-border-default px-3 py-2 text-sm disabled:opacity-50'
const labels: Record<string, string> = {
  waiting: 'Eingeladen – wartet',
  expired: 'Abgelaufen',
  connected: 'Gerät offline',
  issuing: 'Freischaltung läuft',
  issued: 'Wartet auf VPN-Gerät',
  uncertain: 'Abgleich erforderlich',
  revoke_pending: 'Sperrung ausstehend',
  revoked: 'Gesperrt',
}

export function EmployeeInvitations() {
  const client = useQueryClient()
  const [search, setSearch] = useState('')
  const [open, setOpen] = useState(false)
  const [detail, setDetail] = useState<EmployeeInvitationView | null>(null)
  const [result, setResult] = useState<{ id: string; link: string } | null>(null)
  const [message, setMessage] = useState('')
  const [mailAccount, setMailAccount] = useState('')
  const accounts = useQuery({
    queryKey: ['mail', 'accounts'],
    queryFn: () => api.listMailAccounts(),
    enabled: open && !!result,
  })
  const identities = useQuery({
    queryKey: ['mail', 'identities', mailAccount],
    queryFn: () => api.listMailIdentities(mailAccount),
    enabled: !!mailAccount && open,
  })
  const [qr, setQr] = useState('')
  const dialog = useRef<HTMLDialogElement>(null)
  const list = useQuery({
    queryKey: ['employee-invitations'],
    queryFn: () =>
      request<{ invitations: EmployeeInvitationView[]; serverTime: string }>(
        'employee-invitations',
      ),
    refetchInterval: 10000,
  })
  const action = useMutation({
    mutationFn: async ({ path, body }: { path: string; body: unknown }) =>
      request<{ id?: string; link?: string; state?: string }>(path, body),
    onSuccess: async (data) => {
      await client.invalidateQueries({ queryKey: ['employee-invitations'] })
      if (data.id && data.link) {
        setResult({ id: data.id, link: data.link })
        setOpen(true)
      }
      setMessage(
        data.state === 'sent'
          ? 'Mail wurde übergeben.'
          : data.state === 'revoke_pending'
            ? 'Sperrung ausstehend.'
            : 'Vorgang bestätigt.',
      )
    },
    onError: (error) => setMessage(error.message),
  })
  useEffect(() => {
    if (open) dialog.current?.showModal()
    else dialog.current?.close()
  }, [open])
  useEffect(() => {
    let active = true
    setQr('')
    if (result)
      void QRCode.toDataURL(result.link, { width: 168, margin: 4, errorCorrectionLevel: 'M' }).then(
        (value) => {
          if (active) setQr(value)
        },
      )
    return () => {
      active = false
    }
  }, [result])
  function close() {
    setOpen(false)
    setResult(null)
    setDetail(null)
    setMessage('')
  }
  async function copy() {
    if (!result) return
    try {
      await navigator.clipboard.writeText(result.link)
      setMessage('Link kopiert.')
    } catch {
      const field = dialog.current?.querySelector<HTMLInputElement>('[data-link]')
      field?.focus()
      field?.select()
      setMessage('Link markieren und über das Gerätemenü kopieren.')
    }
  }
  const rows = (list.data?.invitations ?? []).filter((row) =>
    `${row.name} ${row.email}`.toLowerCase().includes(search.toLowerCase()),
  )
  return (
    <div className="space-y-4">
      <div className="flex flex-wrap justify-between gap-3">
        <label className="relative max-w-full w-72">
          <Search className="absolute left-3 top-3 h-4 w-4 text-text-tertiary" />
          <input
            aria-label="Mitarbeiter suchen"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            className={`${inputClass} pl-9`}
            placeholder="Suchen …"
          />
        </label>
        <button
          type="button"
          className="flex items-center gap-2 rounded-xl bg-accent px-4 py-2 text-bg-base"
          onClick={() => {
            setOpen(true)
            setDetail(null)
            setResult(null)
          }}
        >
          <Plus size={16} />
          Einladen
        </button>
      </div>
      <Link to="/settings/users" className="inline-block text-sm text-accent">
        Personalverwaltung und Akten öffnen
      </Link>
      {list.isLoading && <p role="status">Einladungen werden geladen …</p>}
      {list.isError && (
        <p role="alert">
          Einladungen konnten nicht geladen werden.{' '}
          <button type="button" onClick={() => void list.refetch()}>
            Erneut versuchen
          </button>
        </p>
      )}
      <div className="rounded-2xl border border-border-subtle bg-bg-elevated/40 overflow-hidden">
        <div className="px-5 py-4 text-xs uppercase tracking-wide text-text-tertiary">
          Team ({new Set(rows.map((row) => row.email)).size})
        </div>
        {!list.isLoading && !rows.length && (
          <p className="px-5 pb-6 text-text-secondary">Noch keine passenden Einladungen.</p>
        )}
        {rows.map((row) => (
          <div
            key={row.id}
            className="flex flex-wrap items-center gap-4 border-t border-border-subtle px-5 py-4"
          >
            <div className="grid h-10 w-10 shrink-0 place-items-center rounded-full bg-accent/15 text-xs text-accent">
              {row.name
                .split(' ')
                .map((s) => s[0])
                .slice(0, 2)
                .join('')}
            </div>
            <div className="min-w-0 flex-1 basis-52">
              <div>{row.name}</div>
              <div className="break-all text-xs text-text-tertiary">{row.email} · Mitarbeiter</div>
              {row.state === 'waiting' || row.state === 'expired' ? (
                <button
                  type="button"
                  className="min-h-10 text-xs text-accent"
                  disabled={action.isPending}
                  onClick={() =>
                    action.mutate({ path: `employee-invitations/${row.id}/rotate`, body: {} })
                  }
                >
                  Link neu senden
                </button>
              ) : null}
            </div>
            <span className="rounded-full border border-border-subtle px-2 py-1 text-xs text-text-secondary">
              {row.device?.name ?? '— noch kein Gerät'}
            </span>
            <div className="min-w-40 text-xs">
              <span
                className={
                  row.state === 'connected'
                    ? 'rounded-full bg-success/10 px-2 py-1 text-success'
                    : row.state === 'revoked'
                      ? 'rounded-full bg-danger/10 px-2 py-1 text-danger'
                      : 'rounded-full bg-warning/10 px-2 py-1 text-warning'
                }
              >
                {row.state === 'connected' &&
                row.checkedAt &&
                Date.parse(list.data?.serverTime ?? '') - Date.parse(row.checkedAt) >= 60000
                  ? 'Abgleich ausstehend'
                  : row.state === 'connected' && row.device?.online
                    ? 'Gerät verbunden'
                    : (labels[row.state] ?? row.state)}
              </span>
              {row.state === 'waiting' && (
                <span>
                  {' '}
                  (
                  {Math.max(
                    0,
                    Math.ceil(
                      (Date.parse(row.expiresAt) - Date.parse(list.data?.serverTime ?? '')) /
                        3600000,
                    ),
                  )}{' '}
                  h übrig)
                </span>
              )}
              {row.checkedAt && (
                <div className="text-text-tertiary">
                  Abgleich: {new Date(row.checkedAt).toLocaleTimeString('de-DE')}
                </div>
              )}
            </div>
            <div className="flex gap-2">
              <button
                type="button"
                className={buttonClass}
                onClick={() => {
                  setDetail(row)
                  setOpen(true)
                  setResult(null)
                }}
              >
                Details
              </button>
              {row.state !== 'revoked' && (
                <button
                  type="button"
                  className={`${buttonClass} flex items-center gap-1`}
                  disabled={action.isPending}
                  onClick={() =>
                    action.mutate({ path: `employee-invitations/${row.id}/revoke`, body: {} })
                  }
                >
                  <Lock size={14} />
                  {row.device ? 'Gerät sperren' : 'Link sperren'}
                </button>
              )}
            </div>
          </div>
        ))}
      </div>
      {!open && message && (
        <p role="status" className="text-sm text-warning">
          {message}
        </p>
      )}
      <dialog
        ref={dialog}
        aria-labelledby="employee-invitation-dialog-title"
        style={{
          position: 'fixed',
          top: '50%',
          left: '50%',
          right: 'auto',
          bottom: 'auto',
          margin: 0,
          transform: 'translate(-50%, -50%)',
        }}
        onKeyDown={(event) => {
          if (event.key !== 'Tab') return
          const elements = Array.from(
            event.currentTarget.querySelectorAll<HTMLElement>(
              'button, input, select, textarea, a[href], [tabindex]',
            ),
          ).filter(
            (element) =>
              element.tabIndex >= 0 &&
              !element.matches(':disabled') &&
              element.getClientRects().length > 0,
          )
          const first = elements[0]
          const last = elements[elements.length - 1]
          if (event.shiftKey && document.activeElement === first) {
            event.preventDefault()
            last?.focus()
          } else if (!event.shiftKey && document.activeElement === last) {
            event.preventDefault()
            first?.focus()
          }
        }}
        onCancel={close}
        onClose={() => setOpen(false)}
        className="m-auto max-h-[calc(100dvh_-_32px)] overflow-y-auto w-[calc(100%_-_32px)] max-w-[460px] rounded-[20px] border border-border-default bg-bg-elevated p-0 text-text-primary backdrop:bg-black/70"
      >
        <div className="flex justify-between border-b border-border-subtle px-6 py-5">
          <h2 id="employee-invitation-dialog-title" className="font-semibold">
            {detail ? 'Einladungsdetail' : 'Mitarbeiter einladen'}
          </h2>
          <button type="button" aria-label="Schließen" onClick={close}>
            <X size={20} />
          </button>
        </div>
        <div className="space-y-4 px-6 py-5">
          {detail ? (
            <>
              <p>{detail.name}</p>
              <p className="text-sm text-text-secondary">
                {labels[detail.state]}. Persönliche Freigabe:{' '}
                {detail.approved ? 'bestätigt' : 'ausstehend'}.
              </p>
              <form
                className="space-y-3"
                onSubmit={(event) => {
                  event.preventDefault()
                  const form = new FormData(event.currentTarget)
                  action.mutate({
                    path: `employee-invitations/${detail.id}/approve`,
                    body: {
                      userId: form.get('userId'),
                      headscaleUserId: form.get('hsId'),
                      personallyVerified: true,
                    },
                  })
                }}
              >
                <label className="block text-sm">
                  Geprüfte Konto-ID
                  <input
                    name="userId"
                    required
                    defaultValue={detail.userId ?? ''}
                    className={inputClass}
                  />
                </label>
                <label className="block text-sm">
                  Geprüfte Headscale-Nutzer-ID
                  <input name="hsId" required inputMode="numeric" className={inputClass} />
                </label>
                <label className="flex gap-2 text-sm">
                  <input type="checkbox" required />
                  Person und Zuordnung persönlich geprüft
                </label>
                <button type="submit" className={buttonClass} disabled={action.isPending}>
                  Freigeben
                </button>
              </form>
              <form
                className="space-y-3 border-t border-border-subtle pt-4"
                onSubmit={(event) => {
                  event.preventDefault()
                  const form = new FormData(event.currentTarget)
                  action.mutate({
                    path: `employee-invitations/${detail.id}/register`,
                    body: { registrationId: form.get('registrationId'), personallyVerified: true },
                  })
                }}
              >
                <label className="block text-sm">
                  iOS: exakte wartende Geräteanfrage
                  <input name="registrationId" required className={inputClass} />
                </label>
                <label className="flex gap-2 text-sm">
                  <input type="checkbox" required />
                  Diese Geräteanfrage persönlich abgeglichen
                </label>
                <button type="submit" className={buttonClass} disabled={action.isPending}>
                  Dieses Gerät freischalten
                </button>
              </form>
              <button
                type="button"
                className={buttonClass}
                onClick={() =>
                  action.mutate({ path: `employee-invitations/${detail.id}/reconcile`, body: {} })
                }
              >
                Status abgleichen
              </button>
            </>
          ) : !result ? (
            <form
              id="invite-form"
              className="space-y-4"
              onSubmit={(event) => {
                event.preventDefault()
                const form = new FormData(event.currentTarget)
                action.mutate({
                  path: 'employee-invitations',
                  body: {
                    name: form.get('name'),
                    email: form.get('email'),
                    platform: form.get('platform'),
                  },
                })
              }}
            >
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <label className="text-xs text-text-secondary">
                  Name
                  <input
                    autoComplete="off"
                    name="name"
                    required
                    maxLength={120}
                    className={`${inputClass} mt-1`}
                  />
                </label>
                <label className="text-xs text-text-secondary">
                  E-Mail
                  <input
                    name="email"
                    type="email"
                    required
                    maxLength={254}
                    className={`${inputClass} mt-1`}
                  />
                </label>
              </div>
              <fieldset>
                <legend className="mb-2 text-xs text-text-secondary">Rolle</legend>
                <div className="grid grid-cols-2 gap-2">
                  <span className="rounded-xl border border-accent/50 bg-accent/10 p-2 text-center text-sm text-accent">
                    Mitarbeiter
                  </span>
                  <button type="button" disabled className={buttonClass}>
                    Kunde · später
                  </button>
                </div>
              </fieldset>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <label className="text-xs text-text-secondary">
                  Gerätetyp (optional)
                  <select name="platform" className={`${inputClass} mt-1`}>
                    {Object.entries(platforms).map(([value, label]) => (
                      <option key={value} value={value}>
                        {label}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="text-xs text-text-secondary">
                  Gültigkeit
                  <input readOnly value="24 Stunden" className={`${inputClass} mt-1`} />
                </label>
              </div>
              <p className="text-xs text-text-tertiary">
                Ein Link für genau ein Gerät. Der Freischalt-Schlüssel gilt ab Abruf zehn Minuten.
                Freigabe durch Dennis erforderlich.
              </p>
            </form>
          ) : (
            <>
              <p className="text-sm">Link erzeugt. Er wird nur jetzt angezeigt.</p>
              <div className="flex flex-wrap gap-4 rounded-xl border border-border-default bg-bg-overlay/50 p-4">
                {qr && <img src={qr} alt="QR-Code der Einladung" width={84} height={84} />}
                <div className="min-w-0 flex-1 basis-40">
                  <div className="flex gap-2">
                    <input
                      data-link
                      readOnly
                      aria-label="Einladungslink"
                      value={result.link}
                      className={`${inputClass} text-xs`}
                    />
                    <button
                      type="button"
                      aria-label="Link kopieren"
                      className="shrink-0 p-2"
                      onClick={() => void copy()}
                    >
                      <Copy size={18} />
                    </button>
                  </div>
                </div>
              </div>
              <form
                className="space-y-2"
                onSubmit={(event) => {
                  event.preventDefault()
                  const form = new FormData(event.currentTarget)
                  action.mutate({
                    path: `employee-invitations/${result.id}/send`,
                    body: {
                      token: result.link.split('/').pop(),
                      accountId: form.get('accountId'),
                      ...(form.get('identityId') ? { identityId: form.get('identityId') } : {}),
                    },
                  })
                }}
              >
                <label className="block text-xs">
                  Absenderkonto
                  <select
                    name="accountId"
                    required
                    value={mailAccount}
                    onChange={(event) => setMailAccount(event.target.value)}
                    className={inputClass}
                  >
                    <option value="">Bitte auswählen</option>
                    {accounts.data?.accounts.map((account) => (
                      <option key={account.id} value={account.id}>
                        {account.email}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="block text-xs">
                  Absenderidentität
                  <select name="identityId" className={inputClass}>
                    <option value="">Standardabsender</option>
                    {identities.data?.identities.map((identity) => (
                      <option key={identity.id} value={identity.id}>
                        {identity.address}
                      </option>
                    ))}
                  </select>
                </label>
                {!accounts.isLoading && !accounts.data?.accounts.length && (
                  <p className="text-sm text-warning">
                    Kein freigegebenes Absenderkonto vorhanden. Link und QR bleiben nutzbar.
                  </p>
                )}
                <button type="submit" className={buttonClass} disabled={action.isPending}>
                  Per Mail senden
                </button>
              </form>
            </>
          )}
          {message && (
            <p role="status" className="text-sm text-warning">
              {message}
            </p>
          )}
        </div>
        <div className="flex justify-end gap-2 border-t border-border-subtle px-6 py-4">
          <button type="button" onClick={close} className={buttonClass}>
            {result ? 'Schließen' : 'Abbrechen'}
          </button>
          {!detail && !result && (
            <button
              type="submit"
              form="invite-form"
              disabled={action.isPending}
              className="rounded-xl bg-accent px-4 py-2 text-sm text-bg-base"
            >
              Link erzeugen
            </button>
          )}
        </div>
      </dialog>
    </div>
  )
}
